<template>
  <div
    class="db-wrapper-field db-wrapper-select-custom"
    :class="[
      $attrs.class,
      {
        'db-has-error': $slots.error,
        'db-has-success': isSuccess,
      },
    ]"
  >
    <div
      class="db-select-custom-wrapper"
      :class="{ 'db-is-open': state.isOpen }"
    >
      <div
        :id="`combo-${uid}`"
        :aria-required="inputAttrs?.required"
        :aria-controls="`listbox-${uid}`"
        :aria-expanded="state.isOpen"
        aria-haspopup="listbox"
        :aria-labelledby="`combo-label-${uid}`"
        :aria-describedby="describedbyAttr"
        :aria-activedescendant="state.isOpen ? `option-${uid}-${state.activeIndex}` : ''"
        class="db-input db-select-custom"
        role="combobox"
        tabindex="0"
        v-bind="inputAttrs"
        v-on:blur="onComboboxBlur"
        v-on:click="onComboboxClick"
        v-on:keydown="onComboboxKeyDown"
      >
        <slot
          name="current"
          :label="selectedIndex !== undefined ? options[selectedIndex].label : null"
        >
          {{ selectedIndex !== undefined ? options[selectedIndex].label : null }}
        </slot>
        <slot name="fieldicon">
          <IconDbChevron
            width="12"
            height="12"
            class="db-chevron"
            :class="{
              'db-rotate-90': state.isOpen,
              'db-rotate-270': !state.isOpen,
            }"
          />
        </slot>
      </div>
      <div
        v-show="state.isOpen"
        :id="`listbox-${uid}`"
        class="db-select-custom-list"
        role="listbox"
        :aria-labelledby="`combo-label-${uid}`"
        tabindex="-1"
        v-on:mousedown="preventMouseDown"
      >
        <div
          v-for="(option, index) in options"
          :id="`option-${uid}-${index}`"
          :key="`${uid}-${index}`"
          role="option"
          class="db-select-custom-option"
          :class="{ 'db-option-current': index === state.activeIndex }"
          :aria-selected="selectedIndex !== undefined && index === selectedIndex"
          :aria-disabled="option.isDisabled"
          v-on:click.stop="optionClick(index)"
          v-on:mousedown="preventMouseDown"
        >
          <slot
            name="option"
            :option="option"
          >
            {{ option.label }}
          </slot>
        </div>
      </div>
    </div>
    <label
      :id="`combo-label-${uid}`"
      class="db-label"
      :for="`combo-${uid}`"
    >
      <span>
        <slot />
        <span
          v-if="inputAttrs?.required"
          class="db-input-required-indicator"
        >
          *
        </span>
      </span>
    </label>
  </div>
  <p
    v-if="$slots.hint"
    :id="`hint-${uid}`"
    class="db-input-hint"
  >
    <slot name="hint" />
  </p>
  <p
    v-if="$slots.error"
    :id="`error-${uid}`"
    class="db-input-error"
  >
    <slot name="error" />
  </p>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { reactive, computed, useSlots } from 'vue'
import {
  useGetActionFromKey,
  useGetUpdatedIndex,
  useGetIndexByLetter,
} from '@/composables/forms/useComboBox'
import {
  useIsElementInView,
  useIsScrollableBlock,
  useMaintainScrollVisibilityBlock,
} from '@/composables/useElementOverflow'
import type { SelectHTMLAttributes } from 'vue'
import type { Options, comboState } from '@/types/components/combobox'

const props = defineProps<{
  /** Is the field in a success state */
  isSuccess?: boolean
  /** Tableau d'options */
  options: Options
  /** binds /w `v-model` directive */
  modelValue: string | number
  /** InputHTMLAttributes /w `id` omitted */
  inputAttrs?: Omit<SelectHTMLAttributes, 'id'>
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: string | number): void
}>()

/** Unique id for label / input binding */
const uid = uuidv4()

const state: comboState = reactive({
  /** Open status of the combo */
  isOpen: false,
  /** Index of the active option (where cursor is, not currently selected) */
  activeIndex: 0,
  /** String typed to filter options */
  searchString: '',
  /** String search timeout  */
  searchTimeout: null,
})

/** Get the slots to check which is on */
const slots = useSlots()

/** Computed `aria-describedby` attr */
const describedbyAttr = computed(() => {
  const describedbyAttrs = [
    ...(slots.hint !== undefined ? [`hint-${uid}`] : []),
    ...(slots.error !== undefined ? [`error-${uid}`] : []),
  ]

  return describedbyAttrs.length > 0 ? describedbyAttrs.toString().replace(',', ' ') : undefined
})

/*
 * This code is an adaptation of WAI component https://www.w3.org/WAI/ARIA/apg/example-index/combobox/combobox-select-only.html
 * which is licensed according to the W3C Software License at
 * https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 */

/** Index of selected index */
const selectedIndex = computed(() => {
  const index = props.options.findIndex((item) => item.value === props.modelValue)
  return index !== -1 ? index : undefined
})

/** Blur from custom select and select the active item as value */
function onComboboxBlur() {
  if (state.isOpen) {
    // setSelectedIndex(state.activeIndex)
    updateMenuState(false, false)
  }
}

/** Click on custom select opens the listbox */
function onComboboxClick() {
  updateMenuState(!state.isOpen, false)
}

/** Handle Keydown event on custom select */
function onComboboxKeyDown(e: KeyboardEvent) {
  const action = useGetActionFromKey(e, state.isOpen)

  switch (action) {
    case 'last':
    case 'first':
      return updateMenuState(true)
    /** Move cursor on  */
    case 'next':
    case 'previous':
    case 'pageup':
    case 'pagedown':
      e.preventDefault()
      setActiveIndex(useGetUpdatedIndex(action, props.options.length, state.activeIndex))
      return
    /** Save and close */
    case 'selectAndClose':
      e.preventDefault()
      /** Do nothing if element is `aria-disabled` */
      if (props.options[state.activeIndex].isDisabled) {
        return
      }
      setSelectedIndex(useGetUpdatedIndex(null, props.options.length, state.activeIndex))
      updateMenuState(false)
      return
    /** Close and don't save */
    case 'close':
      e.preventDefault()
      updateMenuState(false)
      return
    case 'type':
      updateSearchString(e.key)
      updateMenuState(true)
      onComboboxType()
      return
    case 'open':
      e.preventDefault()
      updateMenuState(true)
      return
  }
}

/** Handle letters typing while focused on combobox */
function onComboboxType() {
  // find the index of the first matching option
  const searchIndex = useGetIndexByLetter(props.options, state.searchString, state.activeIndex + 1)

  // if a match was found, go to it
  if (searchIndex >= 0) {
    setActiveIndex(searchIndex)
  }
  // if no matches, clear the timeout and search string
  else {
    if (typeof state.searchTimeout === 'number') {
      window.clearTimeout(state.searchTimeout)
    }
    state.searchString = ''
  }
}

/** Select the option and close component on click */
function optionClick(index: number) {
  /** Do nothing if element is `aria-disabled` */
  if (props.options[index].isDisabled) {
    return
  }
  setSelectedIndex(index)
  updateMenuState(false)
}

/** Prevent blur and closing component to happen on mousedown */
function preventMouseDown(e: Event) {
  e.preventDefault()
}

/** Update `activeIndex` and handle scroll in the scrollable list */
function setActiveIndex(index: number) {
  state.activeIndex = index

  const listboxElem = document.querySelector(`#listbox-${uid}`) as HTMLElement
  const optionElem = document.querySelector(`#option-${uid}-${index}`) as HTMLElement

  // ensure the new option is in view
  if (useIsScrollableBlock(listboxElem) && optionElem) {
    useMaintainScrollVisibilityBlock(optionElem, listboxElem)
  }

  // ensure the new option is visible on screen
  // ensure the new option is in view
  if (!useIsElementInView(optionElem)) {
    optionElem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
}

/** Update `modelValue` and triggers `setActiveIndex` */
function setSelectedIndex(index: number) {
  setActiveIndex(index)
  emit('update:modelValue', props.options[index].value)
}

/** Update `state.isOpen` and can set focus on component */
function updateMenuState(open: boolean, callFocus = true) {
  state.isOpen = open

  const comboElem = document.querySelector(`#combo-${uid}`) as HTMLElement
  state.activeIndex = 0
  callFocus && comboElem.focus()
}

/** Handle state `searchString` and `searchTimeout` while typing keys */
function updateSearchString(key: string) {
  // reset typing timeout and start new timeout
  // this allows us to make multiple-letter matches, like a native select
  if (typeof state.searchTimeout === 'number') {
    window.clearTimeout(state.searchTimeout)
  }

  state.searchTimeout = window.setTimeout(() => {
    state.searchString = ''
  }, 500)

  // add most recent letter to saved search string
  state.searchString += key
}
</script>

<style>
@import '@/assets/styles/commons/forms/_inputs-field.css';
@import '@/assets/styles/components/forms/_select-custom.css';
</style>
